import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaTimes, FaPaperPlane, FaLightbulb } from 'react-icons/fa';
import { aiAPI } from '../services/api';
import { useAppSettings } from '../context/AppSettingsContext';
import useDraggablePosition from '../hooks/useDraggablePosition';

// Example prompts shown behind the "Prompt ideas" button, for users unsure what
// to ask — grouped loosely by the kind of live data the tool-calling loop can reach.
const PROMPT_IDEAS = [
  'What results are pending approval right now?',
  'Which equipment training is expiring in the next 30 days?',
  'Summarize this month\'s CCV trend',
  'List any overdue or stale cleaning validations',
  'Which risk assessments are rated High or Critical?',
  'Show recent audit trail activity',
];

// Floating "chat with app data" widget, available on every authenticated page.
// Uses the backend tool-calling loop so answers are grounded in real records, not invented.
const AIChatWidget = () => {
  const { appName } = useAppSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIdeas, setShowIdeas] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const launcher = useDraggablePosition({
    storageKey: 'ai-chat-launcher-position',
    size: 56,
    side: 'right',
    bottomOffset: 24 + (window.innerWidth <= 1024 ? 60 : 0)
  });
  const panelWidth = Math.min(360, Math.max(220, window.innerWidth - 32));
  const panelHeight = Math.min(480, Math.max(220, window.innerHeight - 140));
  const panelLeft = Math.min(Math.max(8, launcher.position.left + 56 - panelWidth), Math.max(8, window.innerWidth - panelWidth - 8));
  const panelTopAbove = launcher.position.top - panelHeight - 12;
  const panelTop = panelTopAbove >= 8
    ? panelTopAbove
    : Math.min(launcher.position.top + 68, Math.max(8, window.innerHeight - panelHeight - 8));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Grow the input with its text (2 lines minimum, then scroll past ~6 lines).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 60), 140)}px`;
  }, [input, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await aiAPI.chat(text, history);
      const content = res?.data?.data?.content || 'No response.';
      setMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'AI request failed';
      setMessages((prev) => [...prev, { role: 'assistant', content: `⚠ ${message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const applyIdea = (text) => {
    setInput(text);
    setShowIdeas(false);
    inputRef.current?.focus();
  };

  return (
    <>
      <button
        type="button"
        {...launcher.pointerHandlers}
        onClick={() => {
          if (launcher.consumeDraggedClick()) return;
          setOpen((v) => !v);
        }}
        title={`Ask ${appName} AI about your data. Drag to move.`}
        aria-label={`Ask ${appName} AI about your data; drag to move`}
        style={{
          position: 'fixed', left: launcher.position.left, top: launcher.position.top, right: 'auto', bottom: 'auto', zIndex: 60,
          width: '56px', height: '56px', borderRadius: '50%', border: 'none',
          background: 'var(--primary-color, #2563eb)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--fs-2xl)', cursor: launcher.isDragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
        }}
      >
        {open ? <FaTimes /> : <FaRobot />}
      </button>

      {open && (
        <div style={{
          position: 'fixed', left: panelLeft, top: panelTop, right: 'auto', bottom: 'auto', zIndex: 60,
          width: `${panelWidth}px`, maxWidth: 'calc(100vw - 32px)', height: `${panelHeight}px`, maxHeight: 'calc(100vh - 140px)',
          background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius, 12px)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', fontWeight: 700 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FaRobot /> {appName} AI Assistant</span>
            <button
              type="button"
              onClick={() => setShowIdeas((v) => !v)}
              title="Prompt ideas"
              style={{
                border: '1px solid var(--border-color)', borderRadius: '6px', background: showIdeas ? 'var(--primary-color, #2563eb)' : 'transparent',
                color: showIdeas ? '#fff' : 'var(--text-secondary)', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 400,
              }}
            >
              <FaLightbulb size={13} />
            </button>
          </div>
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {messages.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>
                Ask about products, equipment, risk assessments, results, CCV trends, pending approvals, or training expiry. Answers are grounded in your live data and are advisory only.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                background: m.role === 'user' ? 'var(--primary-color, #2563eb)' : 'var(--bg-color)',
                color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                border: m.role === 'user' ? 'none' : '1px solid var(--border-color)',
                borderRadius: '10px', padding: '0.5rem 0.75rem', maxWidth: '85%', fontSize: 'var(--fs-md)', whiteSpace: 'pre-wrap',
              }}>
                {m.content}
              </div>
            ))}
            {loading && <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Thinking…</div>}
          </div>
          {showIdeas && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', padding: '0.6rem', borderTop: '1px solid var(--border-color)', maxHeight: '140px', overflowY: 'auto' }}>
              {PROMPT_IDEAS.map((idea, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyIdea(idea)}
                  style={{
                    border: '1px solid var(--border-color)', borderRadius: '999px', background: 'var(--bg-color)', color: 'var(--text-primary)',
                    padding: '0.3rem 0.65rem', fontSize: 'var(--fs-sm)', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  {idea}
                </button>
              ))}
            </div>
          )}
          <div style={{ padding: '0.6rem', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask a question…"
                rows={2}
                style={{
                  flex: 1, minWidth: 0, boxSizing: 'border-box', minHeight: '60px', maxHeight: '140px', overflowY: 'auto', resize: 'none',
                  border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.5rem 0.65rem',
                  fontFamily: 'inherit', fontSize: 'var(--fs-md)', lineHeight: 1.4,
                  background: 'var(--bg-color)', color: 'var(--text-primary)'
                }}
              />
              <button
                type="button"
                onClick={send}
                disabled={loading || !input.trim()}
                title="Send"
                style={{ flexShrink: 0, border: 'none', borderRadius: '8px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary-color, #2563eb)', color: '#fff', cursor: 'pointer', opacity: loading || !input.trim() ? 0.6 : 1 }}
              >
                <FaPaperPlane />
              </button>
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>Enter to send · Shift+Enter for a new line</div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
